import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { api, API, formatApiError } from "@/lib/api";
import { SERVICES, TOWNS } from "@/lib/services";
import { useAuth } from "@/context/AuthContext";
import { DashboardNavbar } from "@/components/DashboardNavbar";
import { StatusBadge, TypeBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Package, ShieldAlert, ShieldCheck, Trash2, Upload, CalendarClock, MapPin,
  Loader2, CheckCircle2, Send, Download, X, Megaphone,
} from "lucide-react";

const DOC_SLOTS = [
  { type: "SARS Tax Clearance", expiry: true },
  { type: "B-BBEE Certificate", expiry: true },
  { type: "COIDA Letter of Good Standing", expiry: true },
  { type: "Company Registration Documents", expiry: false },
  { type: "Proof of Address", expiry: false },
  { type: "Company Profile", expiry: false },
];
const BBBEE_LEVELS = ["Level 1", "Level 2", "Level 3", "Level 4", "Level 5", "Level 6", "Level 7", "Level 8", "Non-compliant", "Exempt (EME Affidavit)"];

export default function SupplierDashboard() {
  const { user, refresh } = useAuth();
  const [opps, setOpps] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [typeFilter, setTypeFilter] = useState("All");
  const [quoteFor, setQuoteFor] = useState(null);
  const isApproved = user?.status === "approved";

  const load = useCallback(async () => {
    const [o, q] = await Promise.all([
      api.get("/opportunities", { params: { status: "open" } }),
      api.get("/quotes/mine"),
    ]);
    setOpps(o.data);
    setQuotes(q.data);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const filtered = opps.filter((o) => typeFilter === "All" || o.type === typeFilter);
  const respondedIds = new Set(quotes.map((q) => q.opportunity_id));

  return (
    <div className="min-h-screen bg-slate-50">
      <DashboardNavbar />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <h1 className="font-display text-3xl font-bold text-slate-900">Supplier Dashboard</h1>
          <p className="mt-1 text-slate-500">{user?.company_name}</p>

          {user?.status === "rejected" && (
            <div className="mt-5 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="rejected-banner">
              <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-900">Your account is not approved</p>
                <p className="text-sm text-amber-800">
                  {`Reason: ${user?.reject_reason || "Not specified"}. Please update your documents and contact support.`}
                </p>
              </div>
            </div>
          )}

          <Tabs defaultValue="profile" className="mt-6">
            <TabsList data-testid="supplier-tabs">
              <TabsTrigger value="profile" data-testid="tab-profile">SMME Details</TabsTrigger>
              <TabsTrigger value="opportunities" data-testid="tab-opportunities">Opportunities</TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
              <TabsTrigger value="responses" data-testid="tab-responses">My Responses</TabsTrigger>
              <TabsTrigger value="notices" data-testid="tab-notices">Notice Board</TabsTrigger>
            </TabsList>

            {/* Opportunities */}
            <TabsContent value="opportunities" className="mt-6">
              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-full sm:w-72" data-testid="opp-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All types</SelectItem>
                    <SelectItem value="RFQ">RFQ — Request for Quotation</SelectItem>
                    <SelectItem value="TENDER">Tender</SelectItem>
                    <SelectItem value="RFI">RFI — Request for Information</SelectItem>
                    <SelectItem value="RFP">RFP — Request for Proposal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {filtered.length === 0 && <p className="col-span-full py-10 text-center text-slate-500" data-testid="no-open-opps">No open opportunities of this type right now.</p>}
                {filtered.map((o) => (
                  <div key={o.id} data-testid={`opp-card-${o.id}`} className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                    <div className="flex items-center justify-between">
                      <TypeBadge type={o.type} />
                      <span className="font-mono text-xs text-slate-400">{o.reference}</span>
                    </div>
                    <h4 className="mt-3 font-display text-lg font-bold leading-snug text-slate-900">{o.title}</h4>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-600">{o.description}</p>
                    <div className="mt-3 space-y-1.5 text-xs font-medium text-slate-500">
                      <p className="flex items-center gap-1.5"><Package className="h-3.5 w-3.5" /> {o.category}</p>
                      <p className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Closes {o.closing_date}{o.closing_time ? ` at ${o.closing_time}` : ""}</p>
                      {o.location && <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {o.location}</p>}
                      {o.requirements && <p className="flex items-start gap-1.5"><ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span className="line-clamp-2">{o.requirements}</span></p>}
                      {(o.attachments || []).length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                          {(o.attachments || []).map((a) => (
                            <a key={a.file_id} href={`${API}/files/${a.file_id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-blue-600 hover:underline" data-testid={`opp-attach-dl-${a.file_id}`}><Download className="h-3 w-3" /> {a.filename}</a>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mt-4 flex-1" />
                    {respondedIds.has(o.id) ? (
                      <Button disabled variant="outline" className="mt-2 w-full gap-2" data-testid={`responded-${o.id}`}>
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Response submitted
                      </Button>
                    ) : (
                      <Button className="mt-2 w-full gap-2 bg-slate-900 hover:bg-slate-800" onClick={() => setQuoteFor(o)} data-testid={`respond-${o.id}`}>
                        <Send className="h-4 w-4" /> Submit response
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* My responses */}
            <TabsContent value="responses" className="mt-6">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm" data-testid="responses-table">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Reference</th><th className="px-4 py-3">Opportunity</th>
                      <th className="px-4 py-3">Amount</th><th className="px-4 py-3">Document</th><th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {quotes.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500" data-testid="no-responses">You haven't submitted any responses yet.</td></tr>}
                    {quotes.map((q) => (
                      <tr key={q.id} data-testid={`response-row-${q.id}`}>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{q.opportunity_reference}</td>
                        <td className="px-4 py-3 font-medium text-slate-800">{q.opportunity_title}</td>
                        <td className="px-4 py-3 font-mono">{q.amount != null ? `R ${Number(q.amount).toLocaleString()}` : "—"}</td>
                        <td className="px-4 py-3">{q.document ? <a href={`${api.defaults.baseURL}/files/${q.document.id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline"><Download className="h-3.5 w-3.5" />View</a> : "—"}</td>
                        <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Documents */}
            <TabsContent value="documents" className="mt-6">
              <DocumentsSection user={user} refresh={refresh} onChange={load} />
            </TabsContent>

            {/* Profile */}
            <TabsContent value="profile" className="mt-6">
              <ProfileSection user={user} refresh={refresh} />
            </TabsContent>

            {/* Notice Board */}
            <TabsContent value="notices" className="mt-6">
              <NoticeBoard />
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>

      {quoteFor && <QuoteDialog opp={quoteFor} onClose={() => setQuoteFor(null)} onDone={load} />}
    </div>
  );
}

function QuoteDialog({ opp, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [docId, setDocId] = useState(null);
  const [docName, setDocName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", "Quote Response");
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocId(data.id); setDocName(data.original_filename);
      toast.success("File attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/quotes", { opportunity_id: opp.id, amount: amount ? Number(amount) : null, message, document_id: docId });
      toast.success("Response submitted successfully");
      onClose(); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Respond to {opp.reference}</DialogTitle>
          <DialogDescription>{opp.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Quote amount (ZAR)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 45000" className="mt-1.5" data-testid="quote-amount-input" /></div>
          <div><Label>Message / cover note</Label><Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="mt-1.5" placeholder="Brief description of your offer..." data-testid="quote-message-input" /></div>
          <div>
            <Label>Attach response document (PDF)</Label>
            <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-600 hover:bg-slate-50" data-testid="quote-file-label">
              <Upload className="h-4 w-4" />
              {uploading ? "Uploading..." : docName || "Choose file"}
              <input type="file" className="hidden" onChange={upload} data-testid="quote-file-input" />
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="quote-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting || uploading} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="quote-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit response
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocumentsSection({ user, refresh, onChange }) {
  const [uploading, setUploading] = useState("");
  const [expiryMap, setExpiryMap] = useState({});
  const docs = user?.documents || [];
  const byType = (t) => docs.find((d) => d.doc_type === t);

  const statusFor = (slot) => {
    const doc = byType(slot.type);
    if (!doc) return { key: "missing", label: "Missing", tone: "bg-red-100 text-red-800 border-red-300" };
    if (slot.expiry && doc.expiry_date) {
      const days = Math.ceil((new Date(doc.expiry_date) - new Date()) / 86400000);
      if (days < 0) return { key: "expired", label: "Expired", tone: "bg-red-100 text-red-800 border-red-300", days };
      if (days <= 30) return { key: "expiring", label: `Expiring · ${days}d left`, tone: "bg-amber-100 text-amber-800 border-amber-300", days };
      return { key: "valid", label: `Valid · ${days}d left`, tone: "bg-emerald-100 text-emerald-800 border-emerald-300", days };
    }
    return { key: "uploaded", label: "Uploaded", tone: "bg-emerald-100 text-emerald-800 border-emerald-300" };
  };

  const summary = DOC_SLOTS.reduce((acc, s) => {
    const st = statusFor(s).key;
    if (st === "expiring") acc.expiring += 1;
    if (st === "expired" || st === "missing") acc.expiredMissing += 1;
    if (st === "valid" || st === "uploaded" || st === "expiring") acc.active += 1;
    return acc;
  }, { active: 0, expiring: 0, expiredMissing: 0 });

  const setExpiry = (type, val) => setExpiryMap((m) => ({ ...m, [type]: val }));

  const uploadFor = async (e, slot) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const existing = byType(slot.type);
    const expiry = expiryMap[slot.type] ?? existing?.expiry_date ?? "";
    if (slot.expiry && !expiry) {
      toast.error(`Please set an expiry date for ${slot.type}`);
      e.target.value = "";
      return;
    }
    setUploading(slot.type);
    const fd = new FormData();
    fd.append("file", file); fd.append("doc_type", slot.type);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/profile/documents", { file_id: data.id, doc_type: slot.type, filename: data.original_filename, expiry_date: expiry || null });
      toast.success(`${slot.type} uploaded`);
      await refresh(); await onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(""); e.target.value = ""; }
  };

  const deleteDoc = async (doc) => {
    try { await api.delete(`/profile/documents/${doc.file_id}`); toast.success(`${doc.doc_type} removed`); await refresh(); await onChange(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const Stat = ({ icon: Icon, label, value, tone }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
          <div className="font-display text-2xl font-extrabold text-slate-900">{value}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="documents-section">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat icon={CheckCircle2} label="Active" value={summary.active} tone="bg-emerald-100 text-emerald-600" />
        <Stat icon={CalendarClock} label="Expiring soon" value={summary.expiring} tone="bg-amber-100 text-amber-600" />
        <Stat icon={ShieldAlert} label="Expired / Missing" value={summary.expiredMissing} tone="bg-red-100 text-red-600" />
      </div>

      <div className="space-y-4" data-testid="documents-list">
        {DOC_SLOTS.map((slot, i) => {
          const doc = byType(slot.type);
          const st = statusFor(slot);
          return (
            <div key={slot.type} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`doc-slot-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <ShieldCheck className="h-5 w-5 shrink-0 text-slate-500" />
                    <h4 className="font-display text-base font-bold text-slate-900">{slot.type}</h4>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${st.tone}`} data-testid={`doc-status-${i}`}>{st.label}</span>
                  </div>
                  <p className="mt-1 pl-7 text-xs text-slate-500">
                    {doc ? <>{doc.filename} · uploaded {new Date(doc.uploaded_at || Date.now()).toLocaleDateString()}</> : "Not uploaded yet"}
                  </p>
                </div>
                {doc && (
                  <button onClick={() => deleteDoc(doc)} className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-red-600 hover:text-red-700" data-testid={`doc-delete-${i}`}>
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                )}
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50" data-testid={`doc-choose-label-${i}`}>
                  <span className="rounded border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium">Choose File</span>
                  <span className="truncate text-slate-400">No file chosen</span>
                  <input type="file" className="hidden" onChange={(e) => uploadFor(e, slot)} data-testid={`doc-choose-input-${i}`} />
                </label>

                {slot.expiry ? (
                  <Input type="date" value={expiryMap[slot.type] ?? doc?.expiry_date ?? ""} onChange={(e) => setExpiry(slot.type, e.target.value)} data-testid={`doc-expiry-input-${i}`} />
                ) : <div className="hidden md:block" />}

                <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-slate-500 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-600" data-testid={`doc-replace-${i}`}>
                  <Upload className="h-4 w-4" /> {uploading === slot.type ? "Uploading…" : "Replace"}
                  <input type="file" className="hidden" onChange={(e) => uploadFor(e, slot)} data-testid={`doc-replace-input-${i}`} />
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PROVINCES = ["North West", "Gauteng", "Northern Cape"];

const NOTICE_CATEGORIES = ["Meetings", "Announcements", "Proposals"];
const NOTICE_TONE = {
  Meetings: "bg-indigo-100 text-indigo-800 border-indigo-300",
  Announcements: "bg-amber-100 text-amber-800 border-amber-300",
  Proposals: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

function NoticeBoard() {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  useEffect(() => {
    api.get("/notices").then((r) => setNotices(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const shown = notices.filter((n) => filter === "All" || (n.category || "Announcements") === filter);
  return (
    <div className="space-y-4" data-testid="notice-board">
      <div className="max-w-xs">
        <Label>Filter by type</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="mt-1.5" data-testid="notice-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All notices</SelectItem>
            {NOTICE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {loading && <p className="py-8 text-center text-slate-500">Loading notices…</p>}
      {!loading && shown.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="no-notices">
          <Megaphone className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 font-medium">No {filter === "All" ? "" : filter.toLowerCase() + " "}notices yet. Check back for updates from Wonderstone Mine.</p>
        </div>
      )}
      {shown.map((n) => (
        <div key={n.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`notice-${n.id}`}>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100">
              <Megaphone className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-display text-lg font-bold text-slate-900">{n.title}</h4>
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${NOTICE_TONE[n.category] || NOTICE_TONE.Announcements}`} data-testid={`notice-cat-${n.id}`}>{n.category || "Announcements"}</span>
              </div>
              <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-slate-600">{n.body}</p>
              <p className="mt-2 text-xs font-medium text-slate-400">{n.author} · {new Date(n.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
function ProfileSection({ user, refresh }) {
  const [form, setForm] = useState({
    company_name: user?.company_name || "", contact_person: user?.contact_person || "", phone: user?.phone || "",
    registration_number: user?.registration_number || "", bbbee_level: user?.bbbee_level || "",
    tax_number: user?.tax_number || "", province: user?.province || "", town: user?.town || "", description: user?.description || "",
  });
  const [cats, setCats] = useState([]);
  const [selectedServices, setSelectedServices] = useState(user?.services?.length ? user.services : (user?.sector ? [user.sector] : []));
  const [serviceSearch, setServiceSearch] = useState("");
  const [townSearch, setTownSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const toggleService = (s) =>
    setSelectedServices((p) => {
      if (p.includes(s)) return p.filter((x) => x !== s);
      if (p.length >= 2) { toast.error("You can select a maximum of two sectors"); return p; }
      return [...p, s];
    });

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!user) return;
    setForm({
      company_name: user.company_name || "", contact_person: user.contact_person || "", phone: user.phone || "",
      registration_number: user.registration_number || "", bbbee_level: user.bbbee_level || "",
      tax_number: user.tax_number || "", province: user.province || "", town: user.town || "", description: user.description || "",
    });
    setSelectedServices(user.services?.length ? user.services : (user.sector ? [user.sector] : []));
  }, [user]);

  const save = async () => {
    const required = [
      ["company_name", "Company name"], ["phone", "Phone"], ["registration_number", "CIPC registration no."],
      ["tax_number", "Tax number"], ["province", "Province"], ["town", "Town / Village"],
    ];
    const missing = required.filter(([k]) => !String(form[k] || "").trim()).map(([, label]) => label);
    if (missing.length) { toast.error(`Please complete: ${missing.join(", ")}`); return; }
    setSaving(true);
    try { await api.put("/profile", { ...form, sector: selectedServices[0] || "", services: selectedServices, categories: selectedServices }); await refresh(); toast.success("SMME details saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sectorOptions = Array.from(new Set([...cats, ...SERVICES]));

  return (
    <div className="space-y-6" data-testid="profile-section">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="font-display text-lg font-bold text-slate-900">Company details</h3>
        <p className="text-sm text-slate-500">Complete your SMME profile so buyers can evaluate your business.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div><Label>Company name *</Label><Input value={form.company_name} onChange={set("company_name")} className="mt-1.5" data-testid="profile-company-input" /></div>
          <div><Label>Contact person</Label><Input value={form.contact_person} onChange={set("contact_person")} className="mt-1.5" data-testid="profile-contact-input" /></div>
          <div><Label>Phone *</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5" data-testid="profile-phone-input" /></div>
          <div><Label>CIPC registration no. *</Label><Input value={form.registration_number} onChange={set("registration_number")} className="mt-1.5" data-testid="profile-cipc-input" /></div>
          <div><Label>Tax number *</Label><Input value={form.tax_number} onChange={set("tax_number")} className="mt-1.5" data-testid="profile-tax-input" /></div>
          <div><Label>BBBEE level</Label>
            <Select value={form.bbbee_level} onValueChange={(v) => setForm((f) => ({ ...f, bbbee_level: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="profile-bbbee-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{BBBEE_LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Province *</Label>
            <Select value={form.province} onValueChange={(v) => setForm((f) => ({ ...f, province: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="profile-province-select"><SelectValue placeholder="Select province" /></SelectTrigger>
              <SelectContent>{PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Town / Village * (choose one)</Label>
              <span className="text-xs font-medium text-slate-500" data-testid="town-count">{form.town ? "1" : "0"}/1 selected</span>
            </div>
            {form.town && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="selected-town">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 py-1 pl-3 pr-1.5 text-sm text-amber-900" data-testid={`selected-town-${form.town}`}>
                  {form.town}
                  <button type="button" onClick={() => setForm((f) => ({ ...f, town: "" }))} className="rounded-full p-0.5 text-amber-600 hover:bg-amber-100" data-testid="remove-town">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
            )}
            <Input placeholder="Search town / village..." value={townSearch} onChange={(e) => setTownSearch(e.target.value)} className="mt-3" data-testid="town-search-input" />
            <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200" data-testid="towns-list">
              {TOWNS.filter((t) => t.toLowerCase().includes(townSearch.toLowerCase())).map((t) => {
                const checked = form.town === t;
                return (
                  <label key={t} className="flex cursor-pointer items-center gap-2.5 border-b border-slate-100 px-3 py-2 text-sm last:border-0 hover:bg-slate-50" data-testid={`profile-town-${t}`}>
                    <Checkbox checked={checked} onCheckedChange={() => setForm((f) => ({ ...f, town: checked ? "" : t }))} />
                    <span className="text-slate-700">{t}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Sector * (choose up to 2)</Label>
              <span className="text-xs font-medium text-slate-500" data-testid="services-count">{selectedServices.length}/2 selected</span>
            </div>
            {selectedServices.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="selected-services">
                {selectedServices.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 py-1 pl-3 pr-1.5 text-sm text-amber-900" data-testid={`selected-service-${s}`}>
                    {s}
                    <button type="button" onClick={() => toggleService(s)} className="rounded-full p-0.5 text-amber-600 hover:bg-amber-100" data-testid={`remove-service-${s}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <Input placeholder="Search sectors..." value={serviceSearch} onChange={(e) => setServiceSearch(e.target.value)} className="mt-3" data-testid="service-search-input" />
            <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200" data-testid="services-list">
              {sectorOptions.filter((s) => s.toLowerCase().includes(serviceSearch.toLowerCase())).map((s) => {
                const checked = selectedServices.includes(s);
                const disabled = !checked && selectedServices.length >= 2;
                return (
                  <label key={s} className={`flex items-center gap-2.5 border-b border-slate-100 px-3 py-2 text-sm last:border-0 ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:bg-slate-50"}`} data-testid={`profile-service-${s}`}>
                    <Checkbox checked={checked} disabled={disabled} onCheckedChange={() => toggleService(s)} />
                    <span className="text-slate-700">{s}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="gap-2 bg-slate-900 hover:bg-slate-800" data-testid="profile-save-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save SMME details
      </Button>
    </div>
  );
}
